import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { User, Users, GraduationCap, ArrowRight, Check } from 'lucide-react';
import { motion } from 'framer-motion';

const pricingPaths = [
  {
    icon: GraduationCap,
    title: 'Teams & Schools',
    subtitle: 'Group workshops',
    description: 'Build mental toughness, team culture, and performance mindsets across your program.',
    features: [
      '60-90 minute team workshops',
      'Focus, confidence, and resilience training',
      'Sport-specific mental strategies',
      'Multi-session packages available',
      'Custom school presentations',
    ],
    priceRange: 'Scoped on a Planning Call.',
    packageNote: 'Volume discounts on multi-session packages. AI Coach add-ons available.',
    cta: 'Explore Team Workshops',
    popular: false,
  },
  {
    icon: User,
    title: 'Individual Athletes',
    subtitle: 'Middle school through college',
    description: 'One-on-one mental performance coaching tailored to your sport, position, and goals.',
    features: [
      'Personalized 60-minute sessions',
      'Pre-performance routines & rituals',
      'Self-talk and confidence building',
      'Visualization and mental rehearsal',
      'Performance anxiety management',
      'Full Apex AI app access between sessions',
    ],
    priceRange: 'Starting at $175/session',
    packageNote: '3- and 6-session packages — Apex AI Coach access included between sessions.',
    cta: 'Start Your Mental Training',
    popular: true,
  },
  {
    icon: Users,
    title: 'Parents & Coaches',
    subtitle: 'Support & guidance',
    description: 'Learn how to support athletes\' mental game without adding pressure or burnout.',
    features: [
      'Parent consultation sessions',
      'Coach program consultations',
      'Communication strategies',
      'Mental toughness frameworks',
      'Tools to build confidence & resilience',
      'Full Apex AI app access between sessions',
    ],
    priceRange: 'Starting at $175/session',
    packageNote: '45–60 minute consultations — multi-session packages include Apex AI Coach access between sessions.',
    cta: 'Book a Discovery Call (Parent/Coach)',
    popular: false,
  },
];

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
};

export const SimplifiedPricing = ({ calendlyLink }) => {
  return (
    <section
      id="pricing"
      className="py-20 sm:py-24 bg-white"
      data-testid="pricing-section"
    >
      <div className="max-w-[1200px] mx-auto px-5 sm:px-6">
        <motion.div {...fadeIn} className="text-center mb-14">
          <div className="font-mono-accent uppercase tracking-[0.25em] text-[10px] text-[#004C54] font-bold mb-3">
            Coaching Packages
          </div>
          <h2 className="font-heading text-[2rem] sm:text-[2.5rem] font-bold tracking-tight text-[#0a0d0e] mb-4">
            Outcome-Based Coaching with Coach Lewis
          </h2>
          <p className="text-[16px] text-[#3a3a3a] max-w-[58ch] mx-auto">
            Three clear paths &mdash; for the athlete, the people around them, and the teams they play for. Every multi-session package includes the Apex AI Coach app between sessions.
          </p>
        </motion.div>

        <motion.div
          {...fadeIn}
          className="max-w-[700px] mx-auto mb-10 rounded-2xl border border-[#004C54]/20 bg-gradient-to-r from-[#F0F4F4] via-white to-[#E6F4F6] px-5 py-4 sm:px-6 sm:py-5"
          data-testid="pricing-bundle-callout"
        >
          <div className="flex items-start sm:items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#004C54] text-white flex items-center justify-center shrink-0">
              <Check className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-bold mb-0.5">
                Now included
              </div>
              <div className="font-heading text-[15px] sm:text-base font-bold text-[#0a0d0e] leading-snug">
                Multi-session packages include the Apex AI app
              </div>
              <p className="text-[13px] text-foreground/65 mt-0.5">
                AI Coach, Pre-Game Prep, Journal, and Progress dashboard — yours between sessions, for the length of your package.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-7 mb-12 lg:items-stretch">
          {pricingPaths.map((path, i) => {
            const Icon = path.icon;
            return (
              <motion.div
                key={path.title}
                {...fadeIn}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={path.popular ? 'lg:scale-[1.04] lg:z-10 relative' : 'relative'}
              >
                {path.popular && (
                  <div
                    aria-hidden="true"
                    className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#004C54]/40 via-[#4FBCC7]/20 to-[#004C54]/40 blur-md opacity-70 pointer-events-none"
                  />
                )}
                <Card className={`h-full relative ${
                  path.popular
                    ? 'border-2 border-[#004C54] shadow-[0_18px_50px_-12px_rgba(0,76,84,0.45)] bg-gradient-to-b from-white to-[#F0F4F4]'
                    : 'border border-border shadow-sm'
                }`}>
                  {path.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-[#004C54] text-white text-xs font-bold px-4 py-1.5 rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  <CardContent className="p-7 flex flex-col h-full">
                    <div className="mb-6">
                      <div className="w-14 h-14 rounded-xl bg-[#004C54] flex items-center justify-center mb-4">
                        <Icon className="h-7 w-7 text-white" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-[#0a0d0e] mb-1">
                        {path.title}
                      </h3>
                      <p className="text-sm text-[#A5ACAF] uppercase tracking-wide font-semibold">
                        {path.subtitle}
                      </p>
                    </div>

                    <p className="text-[15px] text-[#3a3a3a] leading-relaxed mb-6">
                      {path.description}
                    </p>

                    <ul className="space-y-3 mb-6 flex-1">
                      {path.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2.5 text-[14px] text-[#3a3a3a]">
                          <Check className="h-4 w-4 text-[#004C54] mt-0.5 shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="pt-6 border-t border-border">
                      <div className="mb-5">
                        <p className="text-2xl font-bold text-[#004C54] mb-1">
                          {path.priceRange}
                        </p>
                        <p className="text-xs text-[#A5ACAF]">
                          {path.packageNote}
                        </p>
                      </div>

                      <Button
                        asChild
                        className={`w-full h-12 font-semibold rounded-lg ${
                          path.popular
                            ? 'bg-[#004C54] hover:bg-[#003a40] text-white'
                            : 'bg-white border-2 border-[#004C54] text-[#004C54] hover:bg-[#004C54] hover:text-white'
                        }`}
                      >
                        <a href={calendlyLink}>
                          {path.cta}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          {...fadeIn}
          transition={{ delay: 0.4 }}
          className="text-center bg-[#F6F7F8] rounded-2xl p-8 sm:p-10"
        >
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0a0d0e] mb-3">
            Not Sure Which Option Fits?
          </h3>
          <p className="text-[15px] text-[#3a3a3a] max-w-[50ch] mx-auto mb-6">
            Start with a 30-minute Discovery Call. I take on a limited number of athletes, parents, and coaches at a time — the call helps us see if there's a current opening and whether 1:1 is the right next step for you.
          </p>
          <Button
            asChild
            className="bg-[#004C54] hover:bg-[#003a40] text-white font-bold h-12 px-8 rounded-lg"
          >
            <a href={calendlyLink} data-testid="pricing-bottom-book-cta">
              Book a Discovery Call
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default SimplifiedPricing;
